import React, { useState, useRef } from 'react';
import { connect } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import Dropzone from 'react-dropzone';
import Overlay from '../../shared/Overlay';
import { Helmet } from 'react-helmet-async';
import AccessControl from '../../../AccessControl';
import permissions from '../../../permissionsList';
import { cancelText } from '../../shared/SharedInternationalizations';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Button, {
  buttonTypes,
  buttonSizes,
  buttonTextSizes,
} from '../../shared/Button';
import SvgUploadIcon from '../../svg/UploadIcon';
import SVGCheckMarkIcon from '../../svg/CheckMarkIcon';
import { importUnits } from '../../../redux/actions/unitActions';
import UnitCsvTemplate from '../../../assets/unit_import_template.csv?url';

export function UnconnectedUnitsImport(props) {
  const location = useLocation();
  const navigate = useNavigate();
  const { state = {} } = location;
  const previous = state.previous || '/units/';
  const [data, setData] = useState(null);

  const loading = props?.ui?.loading

  function handleFileSelect(params) {
    setData(params);
  }

  function handleSubmit() {
    props.dispatch(importUnits(data, navigate, previous));
  }

  const linkRef = useRef();

  function csvDownload() {
    linkRef.current.click();
  }

  return (
    <Overlay
      rootUrl={previous}
      canClose={true}
      data-test="units-import-overlay">
      <a
        href={UnitCsvTemplate}
        id="download"
        ref={linkRef}
        className="visually-hidden"
        download>
        Download
      </a>
      <FormattedMessage
        id="unitsImort.browserTabInfo"
        defaultMessage="Units | Import"
        description="Done button">
        {(txt) => (
          <Helmet>
            <title>{txt}</title>
          </Helmet>
        )}
      </FormattedMessage>
      <div className="content-container content-container--column">
        <FormattedMessage
          id="unitsImport.exportUnitsTitle"
          defaultMessage="Import Units"
          description="The title for the page to edit a unit"
          data-test="form-header">
          {(txt) => (
            <h1 className="no-margin text-center" data-test="form-header-text">
              {txt}
            </h1>
          )}
        </FormattedMessage>
        <hr />
        <div className="content-display__container" data-test="user-upload">
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-container">
              <div className="item-container__main-container">
                <div className="item-container__secondary-text">
                  <FormattedMessage
                    id="unitsImport.importMainText"
                    defaultMessage="This interface allows for bulk processing and importing of new units into SARA."
                    description="Clears something"
                  />
                  <ul>
                    <FormattedMessage
                      id="unitsImport.importInfoText"
                      defaultMessage="<e>When importing units, be sure to have the data saved in a csv file.</e><e>All units submitted should have a unique name from any other unit, including those already existing in SARA.</e>"
                      description="Clears something"
                      values={{
                        b: <strong>, c: </strong>,
                        e: (msg) => <li>{msg}</li>,
                      }}
                    />
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="content-display__column content-display__column--1-2">
            <div className="content-display__item-pill-container">
              <div className="item-container__main-container">
                <div className="item-container__main-text">
                  <FormattedMessage
                    id="unitsImport.sampleFileHeaderText"
                    defaultMessage="Sample CSV File"
                    description="Clears something"
                  />
                </div>
                <div className="item-container__secondary-text">
                  <FormattedMessage
                    id="unitsImport.sampleFileText"
                    defaultMessage="To ensure that the imported data will work smoothly in SARA, please use this sample CSV file to input your units for importing."
                    description="Clears something"
                  />
                </div>
              </div>
              <div className="item-container__info-container">
                <Button
                  buttonType={buttonTypes.secondary}
                  text={
                    <FormattedMessage
                      id="unitsImport.downloadFileText"
                      defaultMessage="Download File"
                      description="Opens editing something"
                    />
                  }
                  onClick={csvDownload}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15 js-users-import-sample-file-button"
                  data-test="download-file-button"
                />
              </div>
            </div>
          </div>
          <div className="content-display__item-container width-100">
            <Dropzone
              accept="application/vnd.ms-excel, text/csv"
              onDrop={(acceptedFiles) => {
                handleFileSelect(acceptedFiles);
              }}>
              {({
                getRootProps,
                getInputProps,
                isDragReject,
                acceptedFiles,
              }) => (
                <div
                  className={`item-container__main-container dropzone-area text-large ${
                    acceptedFiles.length > 0
                      ? 'dropzone-area--item-selected'
                      : ''
                  }`}
                  {...getRootProps()}>
                  <input {...getInputProps()} />
                  {acceptedFiles.length === 0 ? <SvgUploadIcon /> : ''}
                  {!isDragReject && acceptedFiles.length === 0 && (
                    <FormattedMessage
                      id="unitsImport.dropzoneUploadText"
                      defaultMessage="Drag a csv file here or click to upload"
                      description="Clears something"
                    />
                  )}
                  {isDragReject && acceptedFiles.length === 0 && (
                    <FormattedMessage
                      id="unitsImport.dropzoneFileTypeText"
                      defaultMessage="Sorry, this file type will not work. Please try another type."
                      description="Clears something"
                    />
                  )}

                  {acceptedFiles.length > 0 &&
                    acceptedFiles.map((acceptedFile, index) => (
                      <div className="dropzone-area--selected-item" key={index}>
                        <SVGCheckMarkIcon />
                        <p className="margin-left-10">{acceptedFile.name}</p>
                      </div>
                    ))}

                  {acceptedFiles.length > 0 && (
                    <FormattedMessage
                      id="unitsImport.chooseAnotherFileText"
                      defaultMessage="Click again to choose another file or click 'Import Units' below to begin import."
                      description="Clears something"
                    />
                  )}
                </div>
              )}
            </Dropzone>
          </div>
        </div>

        <div className="width-100">
          <p></p>
          <div className="form__section-container form__section-container--spread margin-bottom-25">
            <div className="form__items-group-container" />
            <div className="form__items-group-container">
              <Link
                className="link link-text"
                to={previous}
                data-test="units-upload-form-cancel-link">
                {cancelText}
              </Link>
              <AccessControl
                data-test="access-control"
                permissionMessage={true}
                feature={permissions.static.units.importUnits}>
                <Button
                  text={
                    <FormattedMessage
                      id="unitsImport.importUnitsText"
                      defaultMessage="Import Units"
                      description="Opens editing something"
                    />
                  }
                  onClick={handleSubmit}
                  buttonType={buttonTypes.success}
                  buttonSize={buttonSizes.small}
                  buttonTextSize={buttonTextSizes.mediumSmall}
                  additionalStyles="no-margin margin-left-15 js-import-submit"
                  data-test="submit-button"
                  disabled={loading}
                />
              </AccessControl>
            </div>
          </div>
        </div>
      </div>
    </Overlay>
  );
}

const mapStateToProps = (state) => {
  return {
    ...state,
  }
}

export default connect(mapStateToProps)(UnconnectedUnitsImport);
